/**
 * Turning a stored ASSET_3D (a generation result or a library upload) back into
 * a File the Babylon loaders can open.
 *
 * The Pose 3D editor only ever takes a File, because that is what an upload from
 * the filesystem produces. Dragging a 3D result in, or picking one from the
 * library, has to rejoin that same path rather than grow a second one.
 */

import {
	RunnitNodeRunResult,
	RunnitUpload,
	getRunnitNodeRunResultFilePath,
	getRunnitUploadFilePath,
} from '@run-diffusion/shared';
import { storage } from '@/firebase';

export interface Asset3DSource {
	nodeRunResult?: RunnitNodeRunResult | null;
	upload?: RunnitUpload | null;
}

/**
 * Returns null rather than guessing. The name carries the extension that picks
 * the Babylon loader, so defaulting to .glb hands an OBJ or STL to the glTF
 * parser and surfaces a confusing parse failure instead of a clear one.
 */
export function resolveAsset3DName (source: Asset3DSource): string | null {
	return source.nodeRunResult?.file?.name || source.upload?.name || null;
}

export function resolveAsset3DFilePath (source: Asset3DSource): string | null {
	if (source.nodeRunResult?.file) {
		return getRunnitNodeRunResultFilePath(source.nodeRunResult).filePath;
	}
	if (source.upload?.name) {
		return getRunnitUploadFilePath(source.upload).filePath;
	}
	return null;
}

/**
 * Downloads the original model (never the derived thumbnail) and wraps it in a
 * File named after the asset, so getAsset3DExtension picks the right Babylon
 * loader on the other side.
 */
export async function fetchAsset3DAsFile (source: Asset3DSource): Promise<File> {
	const filePath: string | null = resolveAsset3DFilePath(source);
	if (!filePath) throw new Error('This 3D asset has no downloadable file');
	const name: string | null = resolveAsset3DName(source);
	if (!name) throw new Error('This 3D asset has no file name');

	const url: string = await storage.ref(filePath).getDownloadURL();
	const response = await fetch(url);
	if (!response.ok) throw new Error('Failed to download the 3D asset');
	const blob: Blob = await response.blob();

	return new File([blob], name, {
		type: blob.type || 'application/octet-stream',
	});
}
